<template>
  <PModal
    :open="!!detail"
    :title="title"
    :ui="{
      overlay: 'z-60 bg-black/40',
      content: [
        'mention-detail-modal surface inset-x-0 top-auto bottom-0 z-60 max-h-[85dvh] w-full max-w-none translate-x-0 translate-y-0 divide-y-0 overflow-y-auto rounded-t-lg rounded-b-none px-3 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-xl ring-0 sm:inset-x-auto sm:top-1/2 sm:bottom-auto sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-md sm:pb-4',
        detail?.kind === 'location'
          ? 'sm:max-h-[min(42rem,calc(100dvh-2rem))] sm:w-[min(36rem,calc(100vw-1rem))]'
          : 'sm:max-h-[min(28rem,calc(100dvh-2rem))] sm:w-[min(22rem,calc(100vw-1rem))]',
        'data-[state=open]:animate-[mention-detail-in_300ms_ease-out] data-[state=closed]:animate-[mention-detail-out_300ms_ease-out]'
      ]
    }"
    @update:open="!$event && close()"
  >
    <template #content>
      <div class="mb-1 flex items-center justify-between gap-x-2">
        <h2 class="font-heading min-w-0 truncate text-base">{{ title }}</h2>
        <PButton
          title="Sluiten"
          color="surface"
          size="sm"
          variant="ghost"
          :icon="['fas', 'xmark']"
          class="shrink-0 text-inherit! dark:text-inherit!"
          @click="close"
        />
      </div>

      <div v-if="entity">
        <div v-if="detail?.kind === 'location'" class="mb-2">
          <LocationReadOnlyMap :location="entity as Location" />
        </div>

        <div class="space-y-2 text-sm">
          <div v-for="field in fields" :key="field.label">
            <dt class="font-heading text-sm">{{ field.label }}</dt>
            <dd class="mt-0.5" :class="field.multiline ? 'whitespace-pre-wrap' : ''">
              <a
                v-if="field.mailto"
                :href="`mailto:${field.value}`"
                class="text-primary hover:underline"
              >
                {{ field.value }}
              </a>
              <MentionText
                v-else-if="field.mention"
                class="inline wrap-break-word whitespace-pre-wrap"
                :text="field.value"
                prefer-location-address
              />
              <template v-else>{{ field.value }}</template>
            </dd>
          </div>
        </div>

        <div v-if="action" class="mt-2 space-y-2">
          <PButton
            v-if="action.href"
            :to="action.href"
            variant="outline"
            :title="action.title"
            :icon="action.icon"
            block
          >
            {{ action.title }}
          </PButton>
          <PButton
            v-else
            variant="outline"
            :title="action.title"
            :icon="action.icon"
            block
            @click="action.onClick?.()"
          >
            {{ action.title }}
          </PButton>
        </div>
      </div>
      <p v-else class="text-on-surface/75 dark:text-on-surface-dark/75 text-sm italic">
        Gegevens niet beschikbaar.
      </p>
    </template>
  </PModal>
</template>

<script setup lang="ts">
import { getProjectDisplayName } from "@/helper/models/project-display-helper"
import { computed } from "vue"
import PModal from "@/components/overlay/PModal.vue"
import PButton from "@/components/elements/PButton.vue"
import MentionText from "@/components/mention/MentionText.vue"
import LocationReadOnlyMap from "@/components/mention/LocationReadOnlyMap.vue"
import {
  closeMentionDetail as close,
  mentionDetail as detail
} from "@/composables/mention/use-mention-detail"
import { useMention } from "@/composables/mention/use-mention"
import {
  formatKnownBusinessEntityAddress,
  formatPostalAddress,
  getKnownBusinessEntityMainLocation
} from "@/helper/address/format-postal-address"
import { buildMapsNavigationUrl, openMapsNavigation } from "@/helper/location/location-helpers"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model"
import type { Project } from "@/models/project.model"
import type { KnownBusinessEntity } from "@/models/known-business-entity.model"
import type { Location } from "@/models/location.model"

type Field = {
  label: string
  value: string
  multiline?: boolean
  mailto?: boolean
  mention?: boolean
}
type Action = { title: string; icon: string[]; href?: string; onClick?: () => void }

const TITLES = {
  person: "Contactpersoon",
  project: "Project",
  business: "Bedrijf",
  location: "Locatie"
} as const

const { resolveEntity } = useMention()

const entity = computed(() =>
  detail.value ? resolveEntity(detail.value.kind, detail.value.id) : null
)

const title = computed(() => (detail.value ? TITLES[detail.value.kind] : ""))

const fields = computed<Field[]>(() => {
  const kind = detail.value?.kind
  const model = entity.value
  if (!kind || !model) return []
  const out: Field[] = []
  const push = (label: string, value: string | null | undefined, extra?: Partial<Field>) => {
    if (value) out.push({ label, value, ...extra })
  }

  if (kind === "person") {
    const contact = model as KnownBusinessEntityContact
    push("Naam", contact.person?.fullName?.trim())
    push("Telefoon", contact.phoneNumber)
    push("E-mail", contact.email, { mailto: true })
    push("Adres", formatPostalAddress(contact.person?.address), { multiline: true })
  } else if (kind === "business") {
    const business = model as KnownBusinessEntity
    push("Bedrijfsnaam", business.name)
    push("Adres", formatKnownBusinessEntityAddress(business), { multiline: true })
  } else if (kind === "project") {
    const project = model as Project
    push("Projectnaam", getProjectDisplayName(project))
    push("Contactpersoon", project.contact?.person?.fullName?.trim())
    push("Projectopmerking voor dienstverleners", project.descriptionServiceProvider, {
      multiline: true,
      mention: true
    })
  } else {
    const location = model as Location
    push("Locatienaam", location.name?.trim())
    push("Adres", location.address?.trim(), { multiline: true })
  }
  return out
})

const action = computed<Action | null>(() => {
  const kind = detail.value?.kind
  const model = entity.value
  if (!kind || !model) return null

  if (kind === "person") {
    const phone = (model as KnownBusinessEntityContact).getNormalizedPhoneNumber()
    return phone ? { title: "Bellen", icon: ["fas", "phone"], href: `tel:${phone}` } : null
  }
  if (kind === "business") {
    const business = model as KnownBusinessEntity
    const mainLocation = getKnownBusinessEntityMainLocation(business)
    const url = buildMapsNavigationUrl(
      mainLocation?.latitude,
      mainLocation?.longitude,
      formatKnownBusinessEntityAddress(business)
    )
    return url
      ? {
          title: "Navigeren",
          icon: ["fas", "location-arrow"],
          onClick: () => openMapsNavigation(url)
        }
      : null
  }
  if (kind === "location") {
    const location = model as Location
    const url = buildMapsNavigationUrl(
      location.latitude,
      location.longitude,
      location.address?.trim()
    )
    return url
      ? {
          title: "Navigeren",
          icon: ["fas", "diamond-turn-right"],
          onClick: () => openMapsNavigation(url)
        }
      : null
  }
  return null
})
</script>

<style>
/* Animate transform separately from the responsive translate used for centering. */
@keyframes mention-detail-in {
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes mention-detail-out {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(100%);
  }
}
</style>
